import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Options with no image of their own (size, across most of the catalogue)
    // come back with an identical gallery. Swapping the node anyway discards
    // every <img> and makes the browser paint fresh ones, which reads as a
    // flash on mobile and resets the slideshow to the first slide. Only swap
    // when the media, or its order, actually changed.
    if (mediaSignature(newMediaGallery) === mediaSignature(this)) return;

    // Wait for the incoming image to be decodable so the swap paints in one go
    // instead of showing an empty gallery. Guard against a second, faster tap
    // landing while we wait.
    const token = ++this.#updateToken;

    await warmFirstImage(newMediaGallery);

    if (token !== this.#updateToken) return;

    this.replaceWith(newMediaGallery);
  };

  #updateToken = 0;

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * Builds a stable identity for the media a gallery renders, in order.
 *
 * @param {Element} gallery - A media gallery element, either live or freshly parsed.
 * @returns {string} A signature that changes only when the media, or its order, does.
 */
function mediaSignature(gallery) {
  // The zoom dialog lists every media once, keyed by media id and position, and
  // the slideshow never reorders it, so it is the most reliable thing to read.
  const ids = Array.from(gallery.querySelectorAll('.dialog-zoomed-gallery > li[id]')).map((li) => li.id);

  if (ids.length) return `id:${ids.join(',')}`;

  // Zoom is switched off, so there are no per-media ids. Fall back to the image
  // sources, minus the width and version query Shopify appends.
  const sources = Array.from(gallery.querySelectorAll('img'))
    .filter((img) => !img.closest('.dialog-zoomed-gallery'))
    .map((img) => (img.getAttribute('src') ?? '').split('?')[0]);

  return `src:${sources.join(',')}`;
}

/**
 * Requests a gallery's first image ahead of the swap and resolves once it can
 * be painted.
 *
 * The gallery passed in belongs to an inert DOMParser document, where images
 * never load, so this warms the identical request through a throwaway image in
 * the live document. Same srcset and sizes means the browser picks the same
 * candidate and the real element hits cache.
 *
 * @param {Element} gallery - The gallery about to be swapped in.
 * @returns {Promise<void>} Resolves on decode, or after a short cap either way.
 */
function warmFirstImage(gallery) {
  const source = gallery.querySelector('img');

  if (!(source instanceof HTMLImageElement)) return Promise.resolve();

  const warm = new Image();
  const sizes = source.getAttribute('sizes');
  const srcset = source.getAttribute('srcset');
  const src = source.getAttribute('src');

  warm.fetchPriority = 'high';
  if (sizes) warm.sizes = sizes;
  if (srcset) warm.srcset = srcset;
  if (src) warm.src = src;

  if (!warm.src && !warm.srcset) return Promise.resolve();

  // A slow or broken image must never strand the gallery on the old colour, so
  // cap the wait. Past the cap we simply do what the theme did before.
  return Promise.race([
    warm.decode().catch(() => {}),
    new Promise((resolve) => {
      setTimeout(resolve, 400);
    }),
  ]);
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
